import React, { useState, useEffect, useCallback, useRef } from 'react';
import axios from 'axios';
import { Plus, Edit2, Trash2, Upload, FileText, X, Loader2 } from 'lucide-react';
import { toast } from 'sonner';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMPTY_FORM = {
  title: '',
  description: '',
  category: 'Agriculture',
  date: '',
  images: [],
  document_url: '',
  attachments: [],
};

const formatSize = (n) => {
  if (!n) return '';
  if (n < 1024) return `${n} o`;
  if (n < 1024 * 1024) return `${(n / 1024).toFixed(1)} Ko`;
  return `${(n / (1024 * 1024)).toFixed(1)} Mo`;
};

export default function ActivitiesManager() {
  const [activities, setActivities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState(EMPTY_FORM);
  const [imagesInput, setImagesInput] = useState('');
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchActivities = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/activities`);
      setActivities(response.data);
    } catch (error) { console.error(error); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchActivities(); }, [fetchActivities]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const dataToSend = {
        ...formData,
        images: imagesInput.split('\n').map((u) => u.trim()).filter(Boolean),
      };
      if (editingId) {
        await axios.put(`${API}/activities/${editingId}`, dataToSend, { withCredentials: true });
        toast.success('Activité mise à jour');
      } else {
        await axios.post(`${API}/activities`, dataToSend, { withCredentials: true });
        toast.success('Activité créée');
      }
      resetForm();
      fetchActivities();
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la sauvegarde');
    }
  };

  const handleFileUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setUploading(true);
    try {
      const uploaded = [];
      for (const file of files) {
        const fd = new FormData();
        fd.append('file', file);
        const res = await axios.post(`${API}/files/upload`, fd, {
          withCredentials: true,
          headers: { 'Content-Type': 'multipart/form-data' },
        });
        uploaded.push({
          url: res.data.url,
          title: file.name.replace(/\.[^.]+$/, ''),
          filename: file.name,
          size: file.size,
          content_type: file.type,
        });
      }
      setFormData((f) => ({ ...f, attachments: [...(f.attachments || []), ...uploaded] }));
      toast.success(`${uploaded.length} fichier(s) ajouté(s)`);
    } catch (err) {
      console.error(err);
      toast.error("Erreur d'upload du fichier");
    } finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleUpdateAttachment = (index, key, value) => {
    setFormData((f) => {
      const next = [...(f.attachments || [])];
      next[index] = { ...next[index], [key]: value };
      return { ...f, attachments: next };
    });
  };

  const handleRemoveAttachment = (index) => {
    setFormData((f) => ({
      ...f,
      attachments: (f.attachments || []).filter((_, i) => i !== index),
    }));
  };

  const handleEdit = (activity) => {
    setEditingId(activity.activity_id);
    setFormData({
      title: activity.title,
      description: activity.description,
      category: activity.category,
      date: (activity.date || '').split('T')[0],
      images: activity.images || [],
      document_url: activity.document_url || '',
      attachments: activity.attachments || [],
    });
    setImagesInput((activity.images || []).join('\n'));
    setShowForm(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Supprimer cette activité ?')) return;
    try {
      await axios.delete(`${API}/activities/${id}`, { withCredentials: true });
      toast.success('Activité supprimée');
      fetchActivities();
    } catch (error) {
      console.error(error);
      toast.error('Erreur lors de la suppression');
    }
  };

  const resetForm = () => {
    setFormData(EMPTY_FORM);
    setImagesInput('');
    setEditingId(null);
    setShowForm(false);
  };

  return (
    <div data-testid="activities-manager">
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold text-[#2E332D]" style={{ fontFamily: 'Cormorant Garamond, serif' }}>
          Gestion des Activités ({activities.length})
        </h2>
        <button
          onClick={() => (showForm ? resetForm() : setShowForm(true))}
          className="btn-primary inline-flex items-center"
          data-testid="add-activity-button"
        >
          <Plus size={20} className="mr-2" />
          {showForm ? 'Annuler' : 'Ajouter'}
        </button>
      </div>

      {showForm && (
        <form onSubmit={handleSubmit} className="card mb-6">
          <div className="space-y-4">
            <input
              type="text" placeholder="Titre" required
              value={formData.title}
              onChange={(e) => setFormData({ ...formData, title: e.target.value })}
              className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
              data-testid="activity-title-input"
            />
            <textarea
              placeholder="Description" required rows={4}
              value={formData.description}
              onChange={(e) => setFormData({ ...formData, description: e.target.value })}
              className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
              data-testid="activity-description-input"
            />
            <div className="grid sm:grid-cols-2 gap-4">
              <select
                value={formData.category}
                onChange={(e) => setFormData({ ...formData, category: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="activity-category-select"
              >
                <option value="Agriculture">Agriculture</option>
                <option value="Formation">Formation FPT</option>
                <option value="Réseautage">Réseautage</option>
                <option value="Évaluation">Évaluation</option>
                <option value="Autre">Autre</option>
              </select>
              <input
                type="date" required
                value={formData.date}
                onChange={(e) => setFormData({ ...formData, date: e.target.value })}
                className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
                data-testid="activity-date-input"
              />
            </div>
            <textarea
              placeholder="URLs des images (une par ligne)" rows={3}
              value={imagesInput}
              onChange={(e) => setImagesInput(e.target.value)}
              className="w-full px-4 py-2 border border-[#E5E3DB] rounded-lg"
            />

            {/* Attachments uploader */}
            <div className="border-2 border-dashed border-[#E5E3DB] rounded-lg p-4 bg-[#F9F8F6]">
              <label className="block text-sm font-bold text-[#2E332D] mb-3">
                Documents joints (PDF, DOCX, etc.) — consultables par les visiteurs
              </label>
              <input
                ref={fileInputRef}
                type="file"
                multiple
                accept=".pdf,.doc,.docx,.xls,.xlsx,.ppt,.pptx"
                onChange={handleFileUpload}
                disabled={uploading}
                data-testid="activity-attachments-input"
                className="block w-full text-sm text-[#5C665A]
                  file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0
                  file:font-bold file:bg-[#4A6B48] file:text-white
                  hover:file:bg-[#3B5539] file:cursor-pointer"
              />
              {uploading && (
                <p className="text-xs text-[#4A6B48] mt-2 inline-flex items-center gap-1">
                  <Loader2 className="animate-spin" size={12} /> Téléversement en cours…
                </p>
              )}

              {formData.attachments && formData.attachments.length > 0 && (
                <ul className="mt-3 space-y-2" data-testid="activity-attachments-list">
                  {formData.attachments.map((att, idx) => (
                    <li
                      key={`${att.url}-${idx}`}
                      className="flex items-center gap-2 bg-white p-2 rounded border border-[#E5E3DB]"
                      data-testid={`activity-attachment-${idx}`}
                    >
                      <FileText size={16} className="text-[#4A6B48] shrink-0" />
                      <input
                        type="text"
                        value={att.title || ''}
                        onChange={(e) => handleUpdateAttachment(idx, 'title', e.target.value)}
                        placeholder="Titre affiché"
                        className="flex-1 px-2 py-1 text-sm border border-[#E5E3DB] rounded"
                      />
                      <span className="text-xs text-[#5C665A] shrink-0">{formatSize(att.size)}</span>
                      <button
                        type="button"
                        onClick={() => handleRemoveAttachment(idx)}
                        className="p-1 hover:bg-red-50 rounded text-red-500"
                        data-testid={`activity-remove-attachment-${idx}`}
                      >
                        <X size={16} />
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <details className="text-sm">
              <summary className="cursor-pointer text-[#5C665A] hover:text-[#4A6B48]">Lien de document externe (optionnel)</summary>
              <input
                type="url"
                placeholder="https://… (lien direct vers un PDF externe)"
                value={formData.document_url}
                onChange={(e) => setFormData({ ...formData, document_url: e.target.value })}
                className="w-full mt-2 px-4 py-2 border border-[#E5E3DB] rounded-lg"
              />
            </details>

            <div className="flex gap-2">
              <button type="submit" className="btn-primary" data-testid="activity-submit-button">
                {editingId ? 'Mettre à jour' : 'Créer'}
              </button>
              <button type="button" onClick={resetForm} className="btn-secondary">Annuler</button>
            </div>
          </div>
        </form>
      )}

      {loading ? (
        <div className="flex justify-center py-12">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#4A6B48]" />
        </div>
      ) : (
        <div className="space-y-4">
          {activities.length === 0 ? (
            <p className="text-center text-[#5C665A] py-8">
              Aucune activité. Cliquez sur &ldquo;Ajouter&rdquo; pour créer la première.
            </p>
          ) : activities.map((activity) => (
            <div key={activity.activity_id} className="card">
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2 flex-wrap">
                    <span className="text-xs font-medium text-[#D87D4A] px-2 py-1 bg-[#D87D4A]/10 rounded">
                      {activity.category}
                    </span>
                    <span className="text-xs text-[#5C665A]">
                      {new Date(activity.date).toLocaleDateString('fr-FR')}
                    </span>
                    {(activity.attachments || []).length > 0 && (
                      <span className="text-xs inline-flex items-center gap-1 text-[#4A6B48] font-bold">
                        <FileText size={11} />
                        {activity.attachments.length} doc{activity.attachments.length > 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                  <h3 className="text-lg font-bold text-[#2E332D]">{activity.title}</h3>
                  <p className="text-sm text-[#5C665A] mt-1">{activity.description}</p>
                </div>
                <div className="flex gap-2 ml-4">
                  <button onClick={() => handleEdit(activity)} className="p-2 hover:bg-gray-100 rounded">
                    <Edit2 size={18} className="text-[#4A6B48]" />
                  </button>
                  <button onClick={() => handleDelete(activity.activity_id)} className="p-2 hover:bg-gray-100 rounded">
                    <Trash2 size={18} className="text-red-500" />
                  </button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
